import { db } from '../config/firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  addDoc,
  updateDoc,
  doc,
  orderBy,
  limit,
  Timestamp
} from 'firebase/firestore';
import {
  BillingSnapshot,
  BillingPeriod,
  BillingLead
} from '../types/billing.types';
import { 
  format, 
  startOfWeek, 
  endOfWeek,
  subWeeks,
  addDays,
  setHours,
  setMinutes,
  setSeconds,
  setMilliseconds
} from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

export const billingSnapshotService = {
  getWeekLockTime(weekStart: Date): Date {
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 0 });
    const lockTime = addDays(weekEnd, 1); // Monday
    // Set to midnight
    const midnightLockTime = setMilliseconds(
      setSeconds(
        setMinutes(
          setHours(lockTime, 0), 
          0
        ), 
        0
      ), 
      0
    );
    
    // Convert from PST to UTC for storage
    return fromZonedTime(midnightLockTime, 'America/Los_Angeles');
  },

  isWeekLocked(weekStart: Date): boolean {
    const now = new Date();
    const lockTime = this.getWeekLockTime(weekStart);
    return now > lockTime;
  },

  async getWeekSnapshot(projectId: string, weekStart: Date): Promise<BillingSnapshot | null> {
    try {
      const weekStartDate = startOfWeek(weekStart, { weekStartsOn: 0 });
      const weekEndDate = endOfWeek(weekStart, { weekStartsOn: 0 });

      const q = query(
        collection(db, 'billingSnapshots'),
        where('projectId', '==', projectId),
        where('weekStart', '>=', Timestamp.fromDate(weekStartDate)),
        where('weekStart', '<=', Timestamp.fromDate(weekEndDate)),
        limit(1)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;

      const docSnapshot = snapshot.docs[0];
      const data = docSnapshot.data();
      
      return {
        id: docSnapshot.id,
        ...data,
        weekStart: data.weekStart.toDate(),
        weekEnd: data.weekEnd.toDate(),
        lockedAt: data.lockedAt,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      } as BillingSnapshot;
    } catch (error) {
      console.error('Error getting week snapshot:', error);
      return null;
    }
  },

  async createSnapshotFromBilling(billing: BillingPeriod): Promise<BillingSnapshot> {
    try {
      const snapshot: Partial<BillingSnapshot> = {
        projectId: billing.projectId,
        weekStart: billing.periodStart,
        weekEnd: billing.periodEnd,
        lockedAt: Timestamp.now(),
        leads: billing.leads.map(lead => ({
          leadId: lead.leadId,
          parentName: lead.parentName,
          children: lead.children,
          capturedBy: lead.capturedBy,
          capturedByName: lead.capturedByName,
          capturedDate: lead.capturedDate,
          determinationAtSnapshot: lead.currentDetermination,
          currentDetermination: lead.currentDetermination,
          enrolled: lead.enrolled || false,
          enrolledStudents: lead.enrolledStudents || 0,
          status: lead.status
        })),
        summary: billing.summary,
        status: 'locked',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      const docRef = await addDoc(collection(db, 'billingSnapshots'), snapshot);
      
      return {
        id: docRef.id,
        ...snapshot
      } as BillingSnapshot;
    } catch (error) {
      console.error('Error creating snapshot:', error);
      throw error;
    }
  },

  async updateSnapshotStatus(snapshotId: string, status: 'active' | 'locked'): Promise<void> {
    try {
      await updateDoc(doc(db, 'billingSnapshots', snapshotId), {
        status,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating snapshot status:', error);
      throw error;
    }
  },

  async getProjectSnapshots(projectId: string, limitCount: number = 12): Promise<BillingSnapshot[]> {
    try {
      const q = query(
        collection(db, 'billingSnapshots'),
        where('projectId', '==', projectId),
        orderBy('weekStart', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          weekStart: data.weekStart.toDate(),
          weekEnd: data.weekEnd.toDate()
        } as BillingSnapshot;
      });
    } catch (error) {
      console.error('Error getting project snapshots:', error);
      return [];
    }
  },

  async convertSnapshotToBillingPeriod(snapshot: BillingSnapshot): Promise<BillingPeriod> {
    const billingLeads: BillingLead[] = snapshot.leads.map(lead => ({
      leadId: lead.leadId,
      parentName: lead.parentName,
      children: lead.children,
      capturedBy: lead.capturedBy,
      capturedByName: lead.capturedByName,
      capturedDate: lead.capturedDate,
      currentDetermination: lead.currentDetermination,
      determinationHistory: [],
      enrolled: lead.enrolled,
      enrolledStudents: lead.enrolledStudents,
      status: lead.status,
      billableAmount: lead.children.length * 45
    }));

    return {
      projectId: snapshot.projectId,
      periodStart: snapshot.weekStart,
      periodEnd: snapshot.weekEnd,
      leads: billingLeads,
      summary: snapshot.summary,
      adjustments: [] // Adjustments are tracked separately
    };
  },

  async createMissingSnapshots(projectId: string, weeksBack: number = 12): Promise<void> {
    try {
      const today = new Date();
      const currentWeekStart = startOfWeek(today, { weekStartsOn: 0 });

      for (let i = 1; i <= weeksBack; i++) {
        const weekStart = subWeeks(currentWeekStart, i);
        
        // Only create snapshots for weeks that should be locked
        if (this.isWeekLocked(weekStart)) {
          const existingSnapshot = await this.getWeekSnapshot(projectId, weekStart);
          
          if (!existingSnapshot) {
            // Get billing data for that week
            const { billingService } = await import('./billing.service');
            const billing = await billingService.calculateWeeklyBilling(projectId, weekStart);
            
            // Only create snapshot if there's data
            if (billing.leads.length > 0) {
              await this.createSnapshotFromBilling(billing);
              console.log(`Created snapshot for week of ${format(weekStart, 'yyyy-MM-dd')}`);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error creating missing snapshots:', error);
    }
  },

  async getBillingHistory(projectId: string, weeks: number = 12): Promise<BillingPeriod[]> {
    try {
      const history: BillingPeriod[] = [];
      const today = new Date();
      const currentWeekStart = startOfWeek(today, { weekStartsOn: 0 });

      for (let i = 0; i < weeks; i++) {
        const weekStart = subWeeks(currentWeekStart, i);
        
        // Check for snapshot first
        const snapshot = await this.getWeekSnapshot(projectId, weekStart);
        
        if (snapshot) {
          // Use snapshot data
          const billing = await this.convertSnapshotToBillingPeriod(snapshot);
          history.push(billing);
        } else if (i > 0 && this.isWeekLocked(weekStart)) {
          // Create snapshot for locked week without one
          const { billingService } = await import('./billing.service');
          const billing = await billingService.calculateWeeklyBilling(projectId, weekStart);
          
          if (billing.leads.length > 0) {
            const newSnapshot = await this.createSnapshotFromBilling(billing);
            const snapshotBilling = await this.convertSnapshotToBillingPeriod(newSnapshot);
            history.push(snapshotBilling);
          } else {
            history.push(billing);
          }
        } else {
          // Current week or future - use live data
          const { billingService } = await import('./billing.service');
          const billing = await billingService.calculateWeeklyBilling(projectId, weekStart);
          history.push(billing);
        }
      }

      return history;
    } catch (error) {
      console.error('Error getting billing history:', error);
      return [];
    }
  }
};